import { getImageUrl, getMediaFileUrl } from '@/lib/utils';
import { getImageUrlServer } from '@/lib/get-image-url-server';
import { IMAGE_PRESETS } from '@/lib/image-presets';
import { expandArticleEmbedsInContentHtml } from '@/lib/comment-embed-pipeline';
import {
  compactRelatedNewsInContentHtml,
  expandRelatedNewsInContentHtml,
  rewriteRelatedNewsImagesInHtml,
} from '@/lib/related-news';
import { uploadDataUrlAsMedia, uploadRemoteImageAsMedia, type MediaUploadOptions } from '@/lib/upload-media-file';
import { isGifUrl } from '@/lib/image-media-utils';
import {
  collectCaptionPairsFromDocument,
  extractRelativeMediaPath,
} from '@/lib/sync-media-caption';
import { syncMediaCaptions } from '@/lib/admin-api';
import { sanitizeArticleContentHtml } from '@/lib/sanitize-article-content-html';

const MEDIA_ROOTS = [
  'haber',
  'manset',
  'video',
  'icerik',
  'user-submissions',
  'storage',
] as const;

/** Hotlink bırakılacak (GIF/embed); haber scrapeleri CDN’e alınır */
const KEEP_REMOTE_HOST_RE =
  /(?:^|\.)(?:giphy\.com|media\.giphy\.com|tenor\.com|media\.tenor\.com|youtube\.com|ytimg\.com|i\.ytimg\.com|vimeocdn\.com|twimg\.com|facebook\.com|fbcdn\.net|instagram\.com|cdninstagram\.com)/i;

function isExternalSrc(src: string): boolean {
  return (
    src.startsWith('http://') ||
    src.startsWith('https://') ||
    src.startsWith('data:') ||
    src.startsWith('blob:')
  );
}

function absolutizeRemoteSrc(src: string): string {
  const s = src.trim();
  if (s.startsWith('//')) return `https:${s}`;
  return s;
}

function shouldRehostRemoteImageUrl(src: string): boolean {
  const abs = absolutizeRemoteSrc(src);
  if (!/^https?:\/\//i.test(abs)) return false;
  if (isGifUrl(abs)) return false;
  if (extractRelativeMediaPath(abs)) return false;
  try {
    const host = new URL(abs).hostname;
    if (KEEP_REMOTE_HOST_RE.test(host)) return false;
    if (/img\.haber\.com|cloudfront\.net$/i.test(host)) return false;
  } catch {
    return false;
  }
  return true;
}

function parseHtml(html: string): Document | null {
  if (typeof DOMParser === 'undefined') return null;
  return new DOMParser().parseFromString(
    `<!doctype html><html><body>${html}</body></html>`,
    'text/html',
  );
}

function rewriteContentCardMediaPaths(
  doc: Document,
  mapPath: (relative: string) => string | null,
): void {
  doc.body.querySelectorAll('related-news[data-image]').forEach((el) => {
    const raw = el.getAttribute('data-image');
    if (!raw) return;
    const relative = extractRelativeMediaPath(raw);
    if (!relative) return;
    const mapped = mapPath(relative);
    if (mapped) el.setAttribute('data-image', mapped);
  });

  doc.body.querySelectorAll('.content-card, .related-news-card').forEach((card) => {
    const dataImage = card.getAttribute('data-related-news-image');
    if (dataImage) {
      const relative = extractRelativeMediaPath(dataImage);
      if (relative) {
        const mapped = mapPath(relative);
        if (mapped) {
          card.setAttribute('data-related-news-image', mapped);
        }
      }
    }

    const img =
      card.querySelector('.related-news-card__image') ||
      card.querySelector('.content-card-image');
    if (!img) return;

    const src = img.getAttribute('src');
    if (!src || src.startsWith('data:')) return;

    const relative = extractRelativeMediaPath(src);
    if (!relative) return;

    const mapped = mapPath(relative);
    if (mapped) {
      img.setAttribute('src', mapped);
    }
  });
}

function isBlankImgSrc(src: string | null | undefined): boolean {
  const s = (src || '').trim();
  return !s || s === '#' || s === 'null' || s === 'undefined';
}

/** src’siz / sahte img — editörde “boş resim” kutusu üretmesin */
function stripBlankImages(doc: Document): void {
  doc.body.querySelectorAll('img').forEach((img) => {
    const src = img.getAttribute('src');
    const dataPath = img.getAttribute('data-media-path');
    if (isBlankImgSrc(src) && isBlankImgSrc(dataPath)) {
      const parent = img.parentElement;
      img.remove();
      if (
        parent &&
        parent.tagName === 'P' &&
        !parent.textContent?.trim() &&
        parent.querySelectorAll('img').length === 0
      ) {
        parent.remove();
      }
    }
  });
}

/** Editörde gösterim için göreli yolları tam URL'ye çevirir */
export function prepareContentHtmlForEditor(html: string): string {
  if (!html?.trim()) return html;

  const doc = parseHtml(html);
  if (!doc) return html;

  stripBlankImages(doc);

  doc.body.querySelectorAll('img').forEach((img) => {
    const src = img.getAttribute('src');
    if (!src || src.startsWith('data:')) return;

    const relative = extractRelativeMediaPath(src);
    if (!relative) return;

    const displayUrl = getImageUrl(relative);
    if (displayUrl) {
      img.setAttribute('src', displayUrl);
      img.setAttribute('data-media-path', relative);
    }
  });

  rewriteContentCardMediaPaths(doc, (relative) => getImageUrl(relative));

  return doc.body.innerHTML;
}

/** Kayıt öncesi: base64 yükle, tam URL'leri göreli yola indir */
export async function normalizeContentHtmlForStorage(
  html: string,
  options?: { nameHint?: string } & MediaUploadOptions,
): Promise<string> {
  if (!html?.trim()) return html;

  const doc = parseHtml(html);
  if (!doc) return html;

  stripBlankImages(doc);

  const images = Array.from(doc.body.querySelectorAll('img'));

  for (let i = 0; i < images.length; i++) {
    const img = images[i];
    const src = img.getAttribute('src') || '';
    const storedPath = img.getAttribute('data-media-path');

    if (isBlankImgSrc(src) && !storedPath) {
      img.remove();
      continue;
    }

    if (storedPath) {
      img.setAttribute('src', storedPath);
      img.removeAttribute('data-media-path');
      continue;
    }

    if (src.startsWith('data:image')) {
      if (isGifUrl(src)) {
        continue;
      }
      const ext = src.includes('image/webp')
          ? 'webp'
          : src.includes('image/png')
            ? 'png'
            : 'jpg';
      try {
        const baseName =
          options?.nameHint?.trim() ||
          `inline-${i + 1}`;
        const path = await uploadDataUrlAsMedia(
          src,
          `inline-${Date.now()}-${i}.${ext}`,
          'icerik',
          baseName,
          options,
        );
        img.setAttribute('src', path);
      } catch {
        // base64 kalırsa en azından diğer img'ler işlensin
      }
      continue;
    }

    const relative = extractRelativeMediaPath(src);
    if (relative) {
      img.setAttribute('src', relative);
      continue;
    }

    const remoteCandidate = absolutizeRemoteSrc(src);
    if (
      remoteCandidate.startsWith('http://') ||
      remoteCandidate.startsWith('https://')
    ) {
      if (!shouldRehostRemoteImageUrl(remoteCandidate)) {
        // GIF / embed / kendi CDN — hotlink kalsın
        continue;
      }
      try {
        const baseName = options?.nameHint?.trim() || `body-${i + 1}`;
        const path = await uploadRemoteImageAsMedia(
          remoteCandidate,
          `body-${Date.now()}-${i}`,
          'icerik',
          `${baseName}-body-${i + 1}`,
          options,
        );
        if (path?.trim()) {
          img.setAttribute('src', path.replace(/^\/+/, '').replace(/^storage\//i, ''));
          img.removeAttribute('srcset');
          img.removeAttribute('data-src');
        }
      } catch {
        // İndirme başarısızsa kaynak URL kalsın
      }
      continue;
    }

    if (isExternalSrc(src)) {
      continue;
    }
  }

  rewriteContentCardMediaPaths(doc, (relative) => relative);

  doc.body.querySelectorAll('.content-card, .related-news-card').forEach((card) => {
    const img =
      card.querySelector('.related-news-card__image') ||
      card.querySelector('.content-card-image');
    if (!img) return;

    const src = img.getAttribute('src');
    if (!src) return;

    const relative = extractRelativeMediaPath(src);
    if (relative) {
      img.setAttribute('src', relative);
      card.setAttribute('data-related-news-image', relative);
    }
  });

  const captionPairs = collectCaptionPairsFromDocument(doc);
  if (captionPairs.length > 0) {
    try {
      await syncMediaCaptions(captionPairs);
    } catch {
      // Medya senkronu başarısız olsa da içerik kaydedilsin
    }
  }

  return sanitizeArticleContentHtml(
    compactRelatedNewsInContentHtml(doc.body.innerHTML),
  );
}

/** Site ön yüzünde göreli yolları çöz */
export function rewriteContentImageUrlsForDisplay(html: string): string {
  if (!html?.trim()) return html;

  let output = expandRelatedNewsInContentHtml(html);
  output = expandArticleEmbedsInContentHtml(output);
  output = rewriteRelatedNewsImagesInHtml(output, (relative) =>
    getImageUrl(relative),
  );

  const doc = parseHtml(output);
  if (!doc) {
    return sanitizeArticleContentHtml(
      output.replace(
        /(<img\b[^>]*\bsrc=["'])([^"']+)(["'])/gi,
        (_match, prefix, src, suffix) => {
          const relative = extractRelativeMediaPath(src);
          if (!relative) return `${prefix}${src}${suffix}`;
          const url = getImageUrl(relative);
          return `${prefix}${url || src}${suffix}`;
        },
      ),
    );
  }

  doc.body.querySelectorAll('img').forEach((img) => {
    const src = img.getAttribute('src');
    if (!src || src.startsWith('data:')) return;

    const relative = extractRelativeMediaPath(src);
    if (!relative) return;

    const url = getImageUrl(relative, IMAGE_PRESETS.gallery);
    if (url) img.setAttribute('src', url);
    if (!img.hasAttribute('loading')) img.setAttribute('loading', 'lazy');
    if (!img.hasAttribute('decoding')) img.setAttribute('decoding', 'async');
    if (!img.hasAttribute('fetchpriority')) {
      img.setAttribute('fetchpriority', 'low');
    }
  });

  doc.body.querySelectorAll('video').forEach((video) => {
    const src =
      video.getAttribute('data-lexical-upload-video') ||
      video.getAttribute('src');
    if (!src || src.startsWith('data:') || src.startsWith('blob:')) return;

    const relative = extractRelativeMediaPath(src);
    if (!relative) return;

    const url = getMediaFileUrl(relative);
    if (url) {
      video.setAttribute('src', url);
      video.setAttribute('data-lexical-upload-video', relative);
    }
    if (!video.hasAttribute('controls')) video.setAttribute('controls', '');
    if (!video.hasAttribute('playsinline')) video.setAttribute('playsinline', '');
  });

  return sanitizeArticleContentHtml(doc.body.innerHTML);
}

/** SSR: kapak görseli gibi CDN ayarıyla içerik img’lerini çöz */
export async function rewriteContentImageUrlsForDisplayServer(
  html: string,
): Promise<string> {
  if (!html?.trim()) return html;

  let output = expandRelatedNewsInContentHtml(html);
  output = expandArticleEmbedsInContentHtml(output);
  output = rewriteRelatedNewsImagesInHtml(output, (relative) => relative);

  const doc = parseHtml(output);
  const replaceSrc = async (src: string): Promise<string> => {
    const relative = extractRelativeMediaPath(src);
    if (!relative) return src;
    return (await getImageUrlServer(relative, IMAGE_PRESETS.gallery)) || src;
  };

  if (!doc) {
    const matches = [...output.matchAll(/(<img\b[^>]*\bsrc=["'])([^"']+)(["'])/gi)];
    const nextSrcs = await Promise.all(matches.map((m) => replaceSrc(m[2])));
    let out = output;
    for (let i = matches.length - 1; i >= 0; i--) {
      const m = matches[i];
      const next = nextSrcs[i];
      if (next !== m[2]) {
        out =
          out.slice(0, m.index!) +
          `${m[1]}${next}${m[3]}` +
          out.slice(m.index! + m[0].length);
      }
    }
    return sanitizeArticleContentHtml(out);
  }

  const imgs = Array.from(doc.body.querySelectorAll('img'));
  const imgNext = await Promise.all(
    imgs.map(async (img) => {
      const src = img.getAttribute('src');
      if (!src || src.startsWith('data:')) return null;
      return replaceSrc(src);
    }),
  );
  imgs.forEach((img, i) => {
    const next = imgNext[i];
    if (next) img.setAttribute('src', next);
    if (!img.hasAttribute('loading')) img.setAttribute('loading', 'lazy');
    if (!img.hasAttribute('decoding')) img.setAttribute('decoding', 'async');
  });

  const videos = Array.from(doc.body.querySelectorAll('video'));
  for (const video of videos) {
    const src =
      video.getAttribute('data-lexical-upload-video') ||
      video.getAttribute('src');
    if (!src || src.startsWith('data:') || src.startsWith('blob:')) continue;
    const relative = extractRelativeMediaPath(src);
    if (!relative) continue;
    const url = getMediaFileUrl(relative);
    if (url) {
      video.setAttribute('src', url);
      video.setAttribute('data-lexical-upload-video', relative);
    }
    if (!video.hasAttribute('controls')) video.setAttribute('controls', '');
    if (!video.hasAttribute('playsinline')) video.setAttribute('playsinline', '');
  }

  // related-news data-image
  const relatedEls = Array.from(
    doc.body.querySelectorAll('[data-related-news-image], related-news[data-image]'),
  );
  const relatedNext = await Promise.all(
    relatedEls.map(async (el) => {
      const attr = el.hasAttribute('data-related-news-image')
        ? 'data-related-news-image'
        : 'data-image';
      const raw = el.getAttribute(attr);
      if (!raw) return null;
      const relative = extractRelativeMediaPath(raw);
      if (!relative) return null;
      const url = await getImageUrlServer(relative);
      if (!url) return null;
      return { el, attr, url };
    }),
  );
  for (const row of relatedNext) {
    if (row) row.el.setAttribute(row.attr, row.url);
  }

  return sanitizeArticleContentHtml(doc.body.innerHTML);
}
